import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Zap, Award, BookOpen, PlayCircle, BarChart3, MessagesSquare, LogOut, Brain, Calendar as CalendarIcon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Calendar } from "../components/ui/calendar";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [date, setDate] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [e, c] = await Promise.all([api.get("/enrollments/me"), api.get("/courses")]);
        setEnrollments(e.data);
        setRecommended(c.data.filter(co => !e.data.find(en => en.course_id === co.course_id)).slice(0, 3));
      } catch {}
    })();
  }, []);

  return (
    <main className="pt-24 pb-24 bg-[#F8F9FA] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <aside className="lg:col-span-3 space-y-6">
            <div className="ali-card p-6 text-center">
              <img src={user?.picture || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.email}`} alt="" className="w-20 h-20 rounded-full mx-auto bg-orange-100" />
              <h3 className="mt-3 font-satoshi font-bold text-[#0A192F]">{user?.name}</h3>
              <p className="text-xs text-slate-500 font-poppins">{user?.email}</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-orange-50 p-2.5">
                  <p className="font-satoshi font-black text-xl text-[#FF6B35]">{user?.streak || 0}</p>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-poppins">Day streak</p>
                </div>
                <div className="rounded-xl bg-slate-100 p-2.5">
                  <p className="font-satoshi font-black text-xl text-[#0A192F]">{user?.xp || 0}</p>
                  <p className="text-[10px] uppercase tracking-widest text-slate-500 font-poppins">XP</p>
                </div>
              </div>
            </div>

            <div className="ali-card p-3" data-testid="dashboard-calendar">
              <Calendar mode="single" selected={date} onSelect={setDate} className="rounded-md" />
            </div>

            <button onClick={async () => { await logout(); navigate("/"); }} className="w-full ali-btn-outline flex items-center justify-center gap-2" data-testid="dashboard-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </aside>

          {/* Main */}
          <section className="lg:col-span-9 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-gradient-to-br from-[#0A192F] to-[#112240] text-white p-8 relative overflow-hidden">
              <div className="absolute -right-10 -top-10 w-64 h-64 bg-[#FF6B35]/20 rounded-full blur-3xl" />
              <div className="relative">
                <p className="text-xs uppercase tracking-widest text-[#FF6B35] font-poppins font-semibold">Welcome back</p>
                <h1 className="font-satoshi font-black text-3xl mt-2">Hello, {user?.name?.split(" ")[0] || "Learner"} 👋</h1>
                <p className="mt-2 text-white/70 font-inter">You're {user?.streak || 0} days into your streak. Keep going!</p>
                <div className="mt-5 flex gap-3">
                  <Link to="/courses" className="ali-btn-primary" data-testid="dashboard-browse">Browse Courses</Link>
                  <button onClick={() => navigate("/demo")} className="ali-btn-outline" style={{ background: "rgba(255,255,255,0.1)", color: "#fff", borderColor: "rgba(255,255,255,0.2)" }} data-testid="dashboard-demo">Book Demo</button>
                </div>
              </div>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { i: BookOpen, t: "Enrolled", v: enrollments.length, c: "orange" },
                { i: PlayCircle, t: "Lessons done", v: enrollments.reduce((s, e) => s + (e.completed_lessons?.length || 0), 0), c: "emerald" },
                { i: Award, t: "Certificates", v: enrollments.filter(e => e.progress === 100).length, c: "amber" },
                { i: Zap, t: "XP earned", v: user?.xp || 0, c: "fuchsia" },
              ].map((s, i) => (
                <div key={i} className="ali-card p-5" data-testid={`dashboard-stat-${i}`}>
                  <div className={`w-10 h-10 rounded-xl bg-${s.c}-50 grid place-items-center text-${s.c}-600 mb-3`}><s.i className="w-5 h-5" /></div>
                  <p className="font-satoshi font-black text-2xl text-[#0A192F]">{s.v}</p>
                  <p className="text-xs text-slate-500 font-poppins uppercase tracking-widest">{s.t}</p>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-end justify-between mb-4">
                <h2 className="font-satoshi font-bold text-2xl text-[#0A192F]">Continue learning</h2>
                <Link to="/courses" className="text-sm text-[#FF6B35] font-poppins font-semibold">All courses →</Link>
              </div>
              {enrollments.length === 0 ? (
                <div className="ali-card p-10 text-center">
                  <BookOpen className="w-12 h-12 text-[#FF6B35] mx-auto" />
                  <p className="mt-4 font-poppins text-slate-500">You haven't enrolled in any course yet.</p>
                  <Link to="/courses" className="mt-5 inline-block ali-btn-primary">Explore Courses</Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {enrollments.map((e) => (
                    <div key={e.enrollment_id} className="ali-card overflow-hidden cursor-pointer" onClick={() => navigate(`/learn/${e.course_slug}`)} data-testid={`enrollment-${e.course_slug}`}>
                      <div className="flex">
                        <img src={e.course_image} alt="" className="w-32 h-32 object-cover" />
                        <div className="flex-1 p-4">
                          <p className="font-satoshi font-bold text-[#0A192F]">{e.course_title}</p>
                          <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#FF6B35] to-[#FF9061]" style={{ width: `${e.progress}%` }} />
                          </div>
                          <p className="mt-1 text-xs text-slate-500 font-poppins">{e.progress}% complete</p>
                          <button className="mt-2 text-sm text-[#FF6B35] font-poppins font-semibold">Continue →</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="font-satoshi font-bold text-2xl text-[#0A192F] mb-4">Recommended for you</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {recommended.map((c) => (
                  <Link key={c.course_id} to={`/courses/${c.slug}`} className="ali-card overflow-hidden">
                    <img src={c.image_url} alt="" className="h-32 w-full object-cover" />
                    <div className="p-4">
                      <p className="text-xs uppercase tracking-widest text-[#FF6B35] font-poppins font-semibold">{c.category}</p>
                      <p className="mt-1 font-satoshi font-bold text-[#0A192F] text-sm">{c.title}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
